import { useSearchParams } from 'react-router-dom';
import { DataGrid, MonthStepper, shiftMonth, useGridConfig } from '@eo/shared';

import type { AttributionList } from './api/types';
import { makeAttributionGridConfig } from './config/gridConfig';
import { formatHours } from './helpers';
import type { AttributionTabProps } from './types';

// Hours that matched no client belong to no client's page, so the team-wide
// view of where the month's ClickUp time landed lives here.
export function AttributionTab({ attribution, month }: AttributionTabProps) {
  const [, setSearchParams] = useSearchParams();
  const config = useGridConfig(makeAttributionGridConfig, undefined);

  const onMonthShift = (delta: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('month', shiftMonth(month, delta));
      params.delete('page');
      return params;
    });
  };

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <MonthStepper month={month} onShift={onMonthShift} />
        <div data-testid="unmapped-hours" className="flex flex-col gap-1 text-right">
          <span className="text-xs text-text-tertiary">Client work that matched no client</span>
          <span
            className={
              (attribution?.unmappedHours ?? 0) > 0
                ? 'text-2xl leading-[26px] tabular-nums text-destructive'
                : 'text-2xl leading-[26px] tabular-nums text-text-secondary'
            }
          >
            {formatHours(attribution?.unmappedHours ?? 0)}
          </span>
        </div>
      </div>
      <p className="text-xs text-text-tertiary">
        An unmapped list is fixed in ClickUp — tag its tasks with the client, or rename the list
        after the client.
      </p>
      <DataGrid<AttributionList>
        config={config}
        rows={attribution?.lists ?? []}
        pagination={attribution?.pagination ?? null}
      />
    </section>
  );
}
